import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Project, ProjectStatus } from "@models/textToSpeech/project.model";
import {
  EmotionProjectReply,
  ProjectService,
} from "~/app/textToSpeech/project.service";
import { AssetService } from "~/app/asset/asset.service";
import { Asset } from "@models/asset/asset.model";
import { VideoJSDataService } from "~/app/shared/video-jsdata.service";
import {
  AssetDescription,
  SmpteTimestamp,
} from "~/app/shared/components/video-player/FrameAccurateControls";

type ManagedPlaybackSource = "tts" | "original";

@Component({
  selector: "app-emotion-replies",
  templateUrl: "./emotion-replies.component.html",
  styleUrls: ["./emotion-replies.component.scss"],
})
export class EmotionRepliesComponent implements OnInit, OnDestroy {
  public projectId: number;
  public spinner = false;
  public loadFailed = false;
  public project: Project | null = null;
  public dialogues: EmotionProjectReply[] = [];
  public savingDialogueIds = new Set<number>();
  public saveFailedDialogueIds = new Set<number>();
  public savedDialogueIds = new Set<number>();
  public regeneratingAudioIds = new Set<number>();
  public changingSpeedIds = new Set<number>();
  public audioGenerationRunning = false;
  public audioGenerationError = false;
  public audioGenerationQueued = false;
  public assembleMixRunning = false;
  public assembleMixError = false;
  public assembleMixSuccess = false;
  public selectedAsset: Asset;
  public showVideoPlayer = false;
  public isSelectedAssetVideo = false;
  public videoPlayer: any;
  public activePlayback: {
    replyId: number;
    source: ManagedPlaybackSource;
  } | null = null;
  private currentReplyAudio: HTMLAudioElement | null = null;
  // Always appended to the played audio URL (see getReplyAudioCacheBuster). Never reset to
  // null/deleted after use: the main wav is re-uploaded in-place at the same S3 key on every
  // regeneration or speed change, so falling back to the bare URL on a later play would risk
  // hitting a *separate*, stale browser cache entry recorded under that exact bare URL from
  // before the bust.
  private readonly replyAudioCacheBusters = new Map<number, number>();
  private videoSegmentEndListener: (() => void) | null = null;
  private stereoTimeUpdateHandler: (() => void) | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly projectService: ProjectService,
    private readonly assetService: AssetService,
    private readonly videoJSService: VideoJSDataService
  ) {
    this.videoJSService.currentVideoPlayer.subscribe((videoPlayer) => {
      this.videoPlayer = videoPlayer;
    });
  }

  ngOnInit(): void {
    this.projectId = Number(this.route.snapshot.paramMap.get("id"));
    if (!this.projectId) {
      this.router.navigate(["/projects"]);
      return;
    }

    this.loadProjectContext();
    this.loadDialogues();
  }

  public backToProjects(): void {
    this.stopReplyPlayback();
    this.disposeVideoPlayer();
    this.router.navigate(["/projects"]);
  }

  public onSaveDialogue(
    line: EmotionProjectReply,
    translatedDialogue: string
  ): void {
    if (!line?.id || this.savingDialogueIds.has(line.id)) {
      return;
    }

    if (
      `${line.translatedDialogue ?? line.dialogue ?? ""}` ===
      `${translatedDialogue ?? ""}`
    ) {
      return;
    }

    this.savingDialogueIds.add(line.id);
    this.saveFailedDialogueIds.delete(line.id);
    this.savedDialogueIds.delete(line.id);

    this.projectService
      .updateEmotionReplyDialogue(this.projectId, line.id, translatedDialogue)
      .subscribe({
        next: () => {
          line.translatedDialogue = translatedDialogue;
          this.savingDialogueIds.delete(line.id);
          this.savedDialogueIds.add(line.id);
          this.triggerReplyAudioRegeneration(line);
        },
        error: () => {
          this.savingDialogueIds.delete(line.id);
          this.saveFailedDialogueIds.add(line.id);
        },
      });
  }

  public onStyleTransferChanged(
    line: EmotionProjectReply,
    styleTransferEnabled: boolean
  ): void {
    if (!line?.id || line.styleTransferEnabled === styleTransferEnabled) {
      return;
    }

    const previousValue = line.styleTransferEnabled;
    line.styleTransferEnabled = styleTransferEnabled;

    this.projectService
      .updateEmotionReplyStyleTransfer(
        this.projectId,
        line.id,
        styleTransferEnabled
      )
      .subscribe({
        next: () => this.triggerReplyAudioRegeneration(line),
        error: () => {
          line.styleTransferEnabled = previousValue;
        },
      });
  }

  public onRadioEffectChanged(
    line: EmotionProjectReply,
    radioEffectEnabled: boolean
  ): void {
    if (!line?.id || line.radioEffectEnabled === radioEffectEnabled) {
      return;
    }

    const previousValue = line.radioEffectEnabled;
    line.radioEffectEnabled = radioEffectEnabled;

    this.projectService
      .updateEmotionReplyRadioEffect(
        this.projectId,
        line.id,
        radioEffectEnabled
      )
      .subscribe({
        next: () => this.triggerReplyAudioRegeneration(line),
        error: () => {
          line.radioEffectEnabled = previousValue;
        },
      });
  }

  private getReplyAudioCacheBuster(replyId: number): number {
    let buster = this.replyAudioCacheBusters.get(replyId);
    if (!buster) {
      buster = Date.now();
      this.replyAudioCacheBusters.set(replyId, buster);
    }
    return buster;
  }

  private triggerReplyAudioRegeneration(line: EmotionProjectReply): void {
    if (!line?.id) {
      return;
    }
    this.regeneratingAudioIds.add(line.id);
    this.projectService
      .generateEmotionReplyAudio(this.projectId, line.id)
      .subscribe({
        next: (response) => {
          if (response?.isSuccess && response?.data?.audioFilePath) {
            line.audioFilePath = response.data.audioFilePath;
            this.replyAudioCacheBusters.set(line.id, Date.now());
          }
          if (
            response?.isSuccess &&
            response?.data?.radioEffectDetected !== undefined
          ) {
            line.radioEffectDetected = response.data.radioEffectDetected;
          }
          if (response?.isSuccess) {
            if (response?.data?.audioDurationSec !== undefined) {
              line.audioDurationSec = response.data.audioDurationSec ?? null;
            }
            if (response?.data?.isLongAudio !== undefined) {
              line.isLongAudio = !!response.data.isLongAudio;
            }
            if (response?.data?.longAudioDurationDiff !== undefined) {
              line.longAudioDurationDiff =
                response.data.longAudioDurationDiff ?? null;
            }
          }
          this.regeneratingAudioIds.delete(line.id);
        },
        error: () => {
          this.regeneratingAudioIds.delete(line.id);
        },
      });
  }

  public onSpeedRateChanged(
    line: EmotionProjectReply,
    speedRate: number
  ): void {
    if (!line?.id || this.changingSpeedIds.has(line.id)) {
      return;
    }

    const previousSpeedRate = line.speedRate;
    this.changingSpeedIds.add(line.id);

    this.projectService
      .updateEmotionReplySpeedRate(this.projectId, line.id, speedRate)
      .subscribe({
        next: (response) => {
          if (response?.isSuccess) {
            line.speedRate = speedRate;
            if (response?.data?.audioFilePath) {
              line.audioFilePath = response.data.audioFilePath;
              this.replyAudioCacheBusters.set(line.id, Date.now());
            }
            line.audioDurationSec = response?.data?.audioDurationSec ?? null;
            line.isLongAudio = !!response?.data?.isLongAudio;
            line.longAudioDurationDiff =
              response?.data?.longAudioDurationDiff ?? null;
          } else {
            line.speedRate = previousSpeedRate;
          }
          this.changingSpeedIds.delete(line.id);
        },
        error: () => {
          line.speedRate = previousSpeedRate;
          this.changingSpeedIds.delete(line.id);
        },
      });
  }

  public get canGenerateEmotionAudio(): boolean {
    if (!this.project || this.audioGenerationRunning) {
      return false;
    }
    return this.project.status !== ProjectStatus.EmotionProcessing;
  }

  public get canAssembleAndMix(): boolean {
    if (!this.project || this.assembleMixRunning) {
      return false;
    }
    const blockedStatuses: string[] = [
      ProjectStatus.EmotionProcessing,
      ProjectStatus.Mixing,
    ];
    if (blockedStatuses.includes(this.project.status as string)) {
      return false;
    }
    return this.dialogues.some((d) => !!d.audioFilePath);
  }

  public get isAssetProxiesGenerated(): boolean {
    return this.selectedAsset?.assetProxy?.length > 0 || false;
  }

  public get isVideoButtonVisible(): boolean {
    return !!this.project?.videoAssetId;
  }

  public get isVideoButtonDisabled(): boolean {
    return !this.isSelectedAssetVideo || !this.isAssetProxiesGenerated;
  }

  public isOriginalPlayEnabledForRow(_line: EmotionProjectReply): boolean {
    const stereo = !!`${this.project?.stereoFilePath ?? ""}`.trim();
    return stereo || (this.showVideoPlayer && !!this.videoPlayer);
  }

  public get startTimeCode(): string {
    return this.project?.startTimeCode !== "00:00:00:00"
      ? this.project?.startTimeCode?.replace(";", ":")
      : this.selectedAsset?.startTimeCode
      ? this.selectedAsset?.startTimeCode?.replace(";", ":")
      : "00:00:00:00";
  }

  public get frameRate(): string {
    return this.isSelectedAssetVideo
      ? this.selectedAsset?.frameRate
      : this.project.frameRate.toString();
  }

  public secondsToHHMMSSFF(seconds: number): string {
    const fps =
      Number(this.frameRate) ||
      (this.project?.frameRate != null ? Number(this.project.frameRate) : NaN);
    const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 25;

    const assetDesc = new AssetDescription("emotion-replies", {}, safeFps);

    const smpte = new SmpteTimestamp(this.startTimeCode, assetDesc);
    const debugSmpte = smpte.toString();
    const secondsStartTimecode = smpte.toAdjustedTime();
    let totalSeconds = Number(seconds) + secondsStartTimecode;

    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
      totalSeconds = 0;
    }

    return SmpteTimestamp.fromTimeWithAdjustments(
      totalSeconds,
      assetDesc
    ).toString();
  }

  public readonly formatReplySecondsToSmpte = (sec: number): string =>
    this.secondsToHHMMSSFF(sec);

  public generateEmotionAudio(): void {
    if (!this.project || this.audioGenerationRunning) {
      return;
    }

    this.audioGenerationRunning = true;
    this.audioGenerationError = false;
    this.audioGenerationQueued = false;

    this.projectService.generateEmotionAudio(this.projectId).subscribe({
      next: (response) => {
        this.audioGenerationRunning = false;
        this.audioGenerationQueued = response?.isSuccess === true;
        this.audioGenerationError = !this.audioGenerationQueued;
        if (this.audioGenerationQueued && this.project) {
          this.project = {
            ...this.project,
            status: ProjectStatus.EmotionProcessing,
          };
        }
      },
      error: () => {
        this.audioGenerationRunning = false;
        this.audioGenerationError = true;
        this.audioGenerationQueued = false;
      },
    });
  }

  public assembleAndMix(): void {
    if (!this.project || this.assembleMixRunning) {
      return;
    }

    this.assembleMixRunning = true;
    this.assembleMixError = false;
    this.assembleMixSuccess = false;

    this.projectService.assembleAndMixEmotionReplies(this.projectId).subscribe({
      next: (response) => {
        this.assembleMixRunning = false;
        this.assembleMixSuccess = response?.isSuccess === true;
        this.assembleMixError = !this.assembleMixSuccess;
        if (this.assembleMixSuccess && this.project) {
          this.project = {
            ...this.project,
            status: ProjectStatus.Mixing,
          };
        }
      },
      error: () => {
        this.assembleMixRunning = false;
        this.assembleMixError = true;
      },
    });
  }

  public onClickVideoBtn(): void {
    if (this.isVideoButtonDisabled) {
      return;
    }
    this.stopReplyPlayback();
    this.showVideoPlayer = !this.showVideoPlayer;
    this.disposeVideoPlayer();
  }

  public onReplyPlayRequested(line: EmotionProjectReply): void {
    if (
      this.activePlayback?.replyId === line.id &&
      this.activePlayback?.source === "tts"
    ) {
      this.stopReplyPlayback();
      return;
    }

    this.stopReplyPlayback();

    if (!line.audioFilePath) {
      return;
    }

    this.activePlayback = { replyId: line.id, source: "tts" };

    if (this.showVideoPlayer && this.videoPlayer) {
      this.videoPlayer.volume(0.1);
      this.videoPlayer.currentTime(line.startSec);
      this.videoPlayer.play().catch(() => {
        // continue with TTS only
      });
    }

    const audioUrl = `${line.audioFilePath}?t=${this.getReplyAudioCacheBuster(
      line.id
    )}`;

    this.currentReplyAudio = new Audio(audioUrl);
    this.currentReplyAudio.addEventListener("ended", () => {
      this.stopReplyPlayback();
    });
    this.currentReplyAudio.addEventListener("error", () => {
      this.stopReplyPlayback();
    });
    this.currentReplyAudio.play().catch(() => {
      this.stopReplyPlayback();
    });
  }

  public onReplyPlayOriginalRequested(line: EmotionProjectReply): void {
    if (
      this.activePlayback?.replyId === line.id &&
      this.activePlayback?.source === "original"
    ) {
      this.stopReplyPlayback();
      return;
    }

    this.stopReplyPlayback();

    const start = Number(line.startSec) || 0;
    const end = Number(line.endSec) || 0;
    if (end <= start + 0.01) {
      return;
    }

    if (this.showVideoPlayer && this.videoPlayer) {
      this.activePlayback = { replyId: line.id, source: "original" };
      this.clearVideoSegmentListener();
      this.videoPlayer.volume(1);
      this.videoPlayer.currentTime(start);
      const endBound = end;
      this.videoSegmentEndListener = () => {
        if (!this.videoPlayer || !this.videoSegmentEndListener) {
          return;
        }
        if (this.videoPlayer.currentTime() >= endBound - 0.02) {
          this.clearVideoSegmentListener();
          this.stopReplyPlayback();
        }
      };
      this.videoPlayer.on("timeupdate", this.videoSegmentEndListener);
      this.videoPlayer.play().catch(() => {
        this.stopReplyPlayback();
      });
      return;
    }

    const stereo = `${this.project?.stereoFilePath ?? ""}`.trim();
    if (!stereo) {
      return;
    }

    this.activePlayback = { replyId: line.id, source: "original" };
    const audio = new Audio(stereo);
    this.currentReplyAudio = audio;

    this.stereoTimeUpdateHandler = () => {
      if (audio.currentTime >= end - 0.02) {
        audio.pause();
        this.clearStereoTimeUpdateListener();
        this.stopReplyPlayback();
      }
    };

    const startPlayback = () => {
      try {
        audio.currentTime = start;
      } catch {
        // ignore seek errors
      }
      if (this.stereoTimeUpdateHandler) {
        audio.addEventListener("timeupdate", this.stereoTimeUpdateHandler);
      }
      audio.play().catch(() => this.stopReplyPlayback());
    };

    audio.addEventListener("ended", () => this.stopReplyPlayback());
    audio.addEventListener("error", () => this.stopReplyPlayback());

    if (audio.readyState >= 1) {
      startPlayback();
    } else {
      audio.addEventListener("loadeddata", startPlayback, { once: true });
    }
  }

  private clearStereoTimeUpdateListener(): void {
    if (this.currentReplyAudio && this.stereoTimeUpdateHandler) {
      this.currentReplyAudio.removeEventListener(
        "timeupdate",
        this.stereoTimeUpdateHandler
      );
    }
    this.stereoTimeUpdateHandler = null;
  }

  private loadProjectContext(): void {
    this.projectService.getById(this.projectId).subscribe({
      next: (project) => {
        this.project = project;
        this.loadSelectedVideoAsset();
      },
      error: () => {
        this.project = null;
        this.selectedAsset = null;
        this.isSelectedAssetVideo = false;
        this.showVideoPlayer = false;
        this.disposeVideoPlayer();
      },
    });
  }

  private loadSelectedVideoAsset(): void {
    if (!this.project?.videoAssetId) {
      this.selectedAsset = null;
      this.isSelectedAssetVideo = false;
      this.showVideoPlayer = false;
      this.disposeVideoPlayer();
      return;
    }

    this.assetService.getAsset(this.project.videoAssetId).subscribe({
      next: (asset) => {
        this.selectedAsset = asset?.id ? asset : null;
        this.isSelectedAssetVideo = this.selectedAsset?.assetType === "video";
        if (!this.isAssetProxiesGenerated || !this.isSelectedAssetVideo) {
          this.showVideoPlayer = false;
          this.disposeVideoPlayer();
        }
      },
      error: () => {
        this.selectedAsset = null;
        this.isSelectedAssetVideo = false;
        this.showVideoPlayer = false;
        this.disposeVideoPlayer();
      },
    });
  }

  private clearVideoSegmentListener(): void {
    if (this.videoPlayer && this.videoSegmentEndListener) {
      this.videoPlayer.off("timeupdate", this.videoSegmentEndListener);
    }
    this.videoSegmentEndListener = null;
  }

  private stopReplyPlayback(): void {
    this.clearVideoSegmentListener();
    this.clearStereoTimeUpdateListener();
    if (this.currentReplyAudio) {
      this.currentReplyAudio.pause();
      this.currentReplyAudio = null;
    }
    this.activePlayback = null;
    if (this.videoPlayer) {
      this.videoPlayer.pause();
      this.videoPlayer.volume(1);
    }
  }

  private disposeVideoPlayer(): void {
    if (this.videoPlayer) {
      this.videoPlayer.dispose();
      this.videoJSService.updateVideoPlayer(false);
      this.videoPlayer = null;
    }
  }

  private loadDialogues(): void {
    this.spinner = true;
    this.loadFailed = false;

    this.projectService.initEmotionReplies(this.projectId).subscribe({
      next: () => {
        this.projectService.getEmotionReplies(this.projectId).subscribe({
          next: (dialogues) => {
            this.dialogues = dialogues;
            this.dialogues.forEach((dialogue) => {
              if (
                !`${dialogue.translatedDialogue ?? ""}`.trim() &&
                `${dialogue.dialogue ?? ""}`.trim()
              ) {
                dialogue.translatedDialogue = `${dialogue.dialogue}`.trim();
              }
            });
            this.savedDialogueIds = new Set(
              (dialogues || [])
                .map((dialogue) => dialogue.id)
                .filter((id) => !!id)
            );
            this.spinner = false;
          },
          error: () => {
            this.loadFailed = true;
            this.spinner = false;
          },
        });
      },
      error: () => {
        this.loadFailed = true;
        this.spinner = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.stopReplyPlayback();
    this.disposeVideoPlayer();
  }
}
